/**
 * Pure view logic for the staff console. The server enforces every rule (field security, roles, totals,
 * reasons); these helpers decide what the console shows and offers, so a role never even sees a control
 * or field it cannot use (defence in depth, QM-C01-002 / QM-C04-004).
 */
import { RESTRICTED_FIELDS } from '@qm/contracts/crm';
import { ORDER_LABEL_KEYS, type PaymentKind } from '@qm/contracts/customers';
import { CHECKLIST_ITEMS } from '@qm/contracts/partners';
import type { Tone } from '../kit/ui';
import type { ApplicationView, CostRow, LeadView, MigrationRun, OrderView, ReconRun, RescueOrder } from '../api';

// ------------------------------------------------------------------ roles
export const STAFF_ROLES = ['staff_admin', 'staff_ops', 'staff_support', 'staff_finance', 'staff_readonly', 'staff_content'] as const;
const has = (roles: readonly string[], ...r: string[]) => roles.some((x) => r.includes(x));
export const isStaff = (roles: readonly string[]) => has(roles, ...STAFF_ROLES);
export const isSuperAdmin = (roles: readonly string[]) => has(roles, 'staff_admin');
/** Finance-only fields: salary, invoice financials, product cost, ledger amounts (QM-C01-002). */
export const isFinance = (roles: readonly string[]) => has(roles, 'staff_finance');
/** MRR / revenue figures in the tenant directory: Finance or Super Admin. */
export const canSeeRevenue = (roles: readonly string[]) => has(roles, 'staff_finance', 'staff_admin');
export const canOperate = (roles: readonly string[]) => has(roles, 'staff_admin', 'staff_ops');
export const canSupport = (roles: readonly string[]) => has(roles, 'staff_admin', 'staff_ops', 'staff_support');
/** Complimentary grants are Super Admin only; Support gets no grant control (QM-C07-001). */
export const canGrantComplimentary = (roles: readonly string[]) => isSuperAdmin(roles);

/** CRM roles implied by staff roles for the field registry (finance/hr). */
export function crmRolesFor(roles: readonly string[]): string[] {
  const out = new Set<string>();
  if (has(roles, 'staff_finance')) out.add('finance');
  if (has(roles, 'staff_hr')) out.add('hr');
  return [...out];
}

type RestrictedEntity = keyof typeof RESTRICTED_FIELDS;
/** Field names a role may not see on an entity. */
export function hiddenFields(entity: RestrictedEntity, roles: readonly string[]): string[] {
  const crm = crmRolesFor(roles);
  const reg = RESTRICTED_FIELDS[entity] as Record<string, readonly string[]>;
  return Object.entries(reg).filter(([, allowed]) => !allowed.some((r) => crm.includes(r))).map(([f]) => f);
}
/** Strip restricted fields client-side too; the server already omits them (0 reads). */
export function visibleRecord<T extends Record<string, unknown>>(entity: RestrictedEntity, row: T, roles: readonly string[]): Partial<T> {
  const hidden = new Set(hiddenFields(entity, roles));
  return Object.fromEntries(Object.entries(row).filter(([k]) => !hidden.has(k))) as Partial<T>;
}
export const ENTITY_REGISTRY: Record<string, RestrictedEntity | undefined> = { employees: 'employee', products: 'product' };

// ------------------------------------------------------------------ orders (QM-C02-001, QM-C06-004)
/** Label key for an order. Complimentary is always "Complimentary", never "Paid". */
export function orderLabelKey(o: Pick<OrderView, 'paymentKind'>): string {
  return ORDER_LABEL_KEYS[o.paymentKind as PaymentKind] ?? 'customers.orders.pending';
}
export const ORDER_TONE: Record<PaymentKind, Tone> = { paid: 'good', complimentary: 'accent', pending: 'warn', refunded: 'neutral', failed: 'bad' };
/** A complimentary order never shows an amount as income. */
export const displayAmountMinor = (o: Pick<OrderView, 'paymentKind' | 'amountMinor'>) => (o.paymentKind === 'complimentary' ? 0 : o.amountMinor);

// ------------------------------------------------------------------ partner approvals (QM-C03)
export function checklistComplete(a: Pick<ApplicationView, 'checklist'>): boolean {
  return CHECKLIST_ITEMS.every((i) => a.checklist?.[i]?.checked === true);
}
export function approvalGate(a: Pick<ApplicationView, 'checklist' | 'readyAt' | 'status'> & Pick<Partial<ApplicationView>, 'isMarketingCompany'>, roles: readonly string[]): { canMarkReady: boolean; canApprove: boolean; canConvert: boolean; reason: 'incomplete' | 'marketing_unknown' | 'not_marketing_company' | 'not_ready' | 'role' | 'decided' | null } {
  const complete = checklistComplete(a);
  if (a.status !== 'pending') return { canMarkReady: false, canApprove: false, canConvert: false, reason: 'decided' };
  // Only marketing companies become partners; anyone else is converted to a customer (owner decision 7 Oct 2026).
  if (a.isMarketingCompany === false) return { canMarkReady: false, canApprove: false, canConvert: isSuperAdmin(roles), reason: 'not_marketing_company' };
  if (!complete) return { canMarkReady: false, canApprove: false, canConvert: false, reason: 'incomplete' };
  if (a.isMarketingCompany !== true) return { canMarkReady: false, canApprove: false, canConvert: false, reason: 'marketing_unknown' };
  if (!a.readyAt) return { canMarkReady: canSupport(roles), canApprove: false, canConvert: false, reason: 'not_ready' };
  if (!isSuperAdmin(roles)) return { canMarkReady: false, canApprove: false, canConvert: false, reason: 'role' };
  return { canMarkReady: false, canApprove: true, canConvert: false, reason: null };
}

// ------------------------------------------------------------------ leads SLA (QM-C02-004)
export function leadSla(l: Pick<LeadView, 'contactedAt' | 'slaDueAt' | 'slaBreachedAt'>, now: Date): 'contacted' | 'ok' | 'due_soon' | 'breached' | 'none' {
  if (l.contactedAt) return 'contacted';
  if (l.slaBreachedAt) return 'breached';
  if (!l.slaDueAt) return 'none';
  const left = Date.parse(l.slaDueAt) - now.getTime();
  if (left <= 0) return 'breached';
  return left <= 30 * 60_000 ? 'due_soon' : 'ok';
}

// ------------------------------------------------------------------ CSV import (QM-C02-003)
export function csvHeader(csv: string): string[] {
  const first = (csv.charCodeAt(0) === 0xfeff ? csv.slice(1) : csv).split(/\r?\n/, 1)[0] ?? '';
  return first.split(',').map((h) => h.trim().replace(/^"|"$/g, '')).filter(Boolean);
}
export const IMPORT_TARGETS = ['name', 'email', 'phone', 'title', 'companyId', 'notes'] as const;
export function autoMapping(headers: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const h of headers) {
    const k = h.toLowerCase().replace(/[^a-z]/g, '');
    const t = IMPORT_TARGETS.find((x) => x.toLowerCase() === k || (x === 'phone' && k.includes('mobile')) || (x === 'name' && k === 'fullname'));
    if (t) out[h] = t;
  }
  return out;
}
export const mappingValid = (m: Record<string, string>) => Object.values(m).some((v) => v === 'email' || v === 'phone');

// ------------------------------------------------------------------ migration (QM-C01-005)
export const HUB_CRM_DATABASE_PREFIX = '1beabaa3';
export function migrationState(r: MigrationRun): { hubSource: boolean; canSignOff: boolean; quarantined: number; orphans: number } {
  const hubSource = [r.source.databaseId, r.source.accountId].some((v) => v.toLowerCase().startsWith(HUB_CRM_DATABASE_PREFIX));
  const quarantined = r.counts.reduce((a, c) => a + c.quarantined, 0);
  const orphans = r.counts.reduce((a, c) => a + c.orphans, 0);
  return { hubSource, quarantined, orphans, canSignOff: !hubSource && r.mode === 'dry_run' && r.status === 'pending_signoff' };
}

// ------------------------------------------------------------------ billing (QM-C06)
export function reconMismatches(r: ReconRun): { ref: string; reason: string }[] {
  if (!r.mismatches_json) return [];
  try {
    const v = JSON.parse(r.mismatches_json) as unknown;
    return Array.isArray(v) ? v.map((m) => ({ ref: String((m as { ref?: unknown; chargeId?: unknown }).ref ?? (m as { chargeId?: unknown }).chargeId ?? ''), reason: String((m as { reason?: unknown }).reason ?? '') })) : [];
  } catch { return []; }
}
export function dunningLabel(s: { status: string; graceEndsAt?: string | null }, now: Date): 'active' | 'dunning' | 'grace' | 'suspended' | 'canceled' {
  if (s.status === 'suspended') return 'suspended';
  if (s.status === 'canceled') return 'canceled';
  if (s.status === 'past_due' || s.status === 'grace') return s.graceEndsAt && Date.parse(s.graceEndsAt) > now.getTime() ? 'grace' : 'dunning';
  return 'active';
}

// ------------------------------------------------------------------ rescue (QM-C07-001)
export const RESCUE_ALERT_MINUTES = 60;
export function rescueSort(rows: readonly RescueOrder[]): RescueOrder[] {
  return [...rows].sort((a, b) => b.ageMinutes - a.ageMinutes);
}
export const overdueRescue = (r: Pick<RescueOrder, 'ageMinutes'>) => r.ageMinutes > RESCUE_ALERT_MINUTES;

// ------------------------------------------------------------------ impersonation (QM-C05-002)
export function impersonationLeft(expiresAt: string, now: Date): number {
  return Math.max(0, Date.parse(expiresAt) - now.getTime());
}

// ------------------------------------------------------------------ costs (QM-C07-004)
export function costTotals(rows: readonly CostRow[]) {
  const cost = rows.reduce((a, r) => a + r.totalCostMinor, 0);
  const price = rows.reduce((a, r) => a + r.priceMinor, 0);
  return { cost, price, margin: price - cost, estimated: rows.filter((r) => r.estimated).length, perAudit: rows.length ? Math.round(cost / rows.length) : 0 };
}
export const componentsSum = (r: CostRow) => r.costs.tokens + r.costs.browser + r.costs.api + r.costs.render + r.costs.email;

// ------------------------------------------------------------------ AI allowlist (QM-C07-005)
export function allowlistChange<T extends { provider: string; model: string; enabled: boolean }>(rows: readonly T[], provider: string, model: string, enabled: boolean): T[] {
  return rows.map((r) => (r.provider === provider && r.model === model ? { ...r, enabled } : r));
}
